import { UploadFile } from "antd/es/upload";
import { defaultAccountsRepository } from "api/repositories/accounts/AccountsRepository";
import { UserInfoResponseModel } from "api/repositories/accounts/model/UserInfoResponse";
import { PathConfigDetailResponseModel } from "api/repositories/pathConfig/model/detail/PathConfigDetailResponseModel";
import { defaultPathConfigRepository } from "api/repositories/pathConfig/PathConfigRepository";
import { SidebarMenuConfigModel } from "api/repositories/sidebarMenu/model/SidebarMenuConfigModel";
import SidebarMenuConfigRepository from "api/repositories/sidebarMenu/SidebarMenuConfigRepository";
import { isEmpty, isUndefined, set } from 'lodash';
import {
  Context,
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import LocalizedStrings from "react-localization";
import { useHistory } from "react-router-dom";
import { getMessage } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";
import { engLocalizedStrings } from "utils/localizedStrings/english";
import { VnLocalizedStrings } from "utils/localizedStrings/vietnam";
import { ContextModel } from "./model/ContextModel";

export const LocalContext: Context<ContextModel> = createContext(
  new ContextModel()
);

export const ContextProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserInfoResponseModel>({});
  const history = useHistory();
  const [info, setInfo] = useState("");
  const token = localStorage.getItem("neci-cms-token");
  const [parameters, setParameters] = useState<
    PathConfigDetailResponseModel["config"]
  >({});
  const [menuConfig, setMenuConfig] =
    useState<SidebarMenuConfigModel>(undefined);
  const [language, setLanguage] = useState<string>("vi");
  const [localStrings, setLocalStrings] = useState<typeof strings>(
    new LocalizedStrings({
      lang: language === "vi" ? VnLocalizedStrings : engLocalizedStrings,
    })
  );
  const [sessionLog, setSessionLog] = useState<{
    sessionId: Array<string>;
    portrait: UploadFile;
    image: string;
  }>({
    sessionId: [],
    portrait: {
      uid: "",
      name: "",
    },
    image: "",
  });
  const [config, setConfig] = useState<Object>({});

  const getRoles = useCallback(async () => {
    try {
      const { data } = await defaultAccountsRepository?.getUser();
      if (data) {
        setUser(data);
        await getSidebar(data?.isAdmin, localStorage?.getItem("language"));
      }
    } catch (error) {
      console.log(error);
      localStorage?.clear();
      getMessage(strings.GlobalMessage.SystemError, 4, "error");
      history?.push("/login");
    }
  }, [history]);

  const getSidebar = useCallback(
    async (role: boolean, language: string) => {
      try {
        const siderConfig = await SidebarMenuConfigRepository?.getSidebarMenu(
          role,
          language
        );
        const parameters = await defaultPathConfigRepository?.configDetail(
          "neci_important_parameters"
        );
        setMenuConfig(siderConfig?.data);
        setParameters(JSON.parse(parameters?.data?.config as string));
        localStorage?.setItem("menu", JSON.stringify(siderConfig?.data));
      } catch (error) {
        console.log(error);
      }
    },
    [user, language]
  );

  const getPartnerInfo = (value: any) => {
    setInfo(value);
  };

  const checkLanguage = async () => {
    const savedLanguage = localStorage?.getItem("language");
    if (savedLanguage === "en") {
      setLocalStrings(
        new LocalizedStrings({
          lang: engLocalizedStrings,
        })
      );
    } else {
      setLocalStrings(
        new LocalizedStrings({
          lang: VnLocalizedStrings,
        })
      );
    }
  };

  const getConfig = async () => {
    try {
      const res = await defaultPathConfigRepository?.configDetail(
        "neci-cms-config"
      );
      if (res?.data) {
        let data = JSON.parse(res?.data?.config as string);
        setConfig(data);
      }
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    if (!isEmpty(token) && !isUndefined(token)) {
      getRoles();
    }
  }, [token]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      checkLanguage();
    }
  }, [language]);

  const isAuthenticated = () => {
    return !!token;
  };

  return (
    <LocalContext.Provider
      value={{
        user: user,
        getPartnerInfo: getPartnerInfo,
        info,
        menuConfig,
        getRoles,
        getSidebar,
        isAuthenticated,
        localStrings,
        setLocalStrings,
        language,
        setLanguage,
        sessionLog,
        setSessionLog,
        parameters,
        setParameters,
        config,
        getConfig
      }}
    >
      {children}
    </LocalContext.Provider>
  );
};

export const AuthenticationContext = () => useContext(LocalContext);
